import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import {
  RiArrowLeftLine,
  RiPrinterLine,
  RiFileTextLine,
  RiUserLine,
  RiPhoneLine,
  RiMapPinLine,
  RiBankLine,
  RiBuildingLine,
  RiUserStarLine,
  RiShoppingCartLine,
  RiMoneyDollarCircleLine,
  RiCalendarLine,
  RiStickyNoteLine,
  RiCheckboxCircleLine,
  RiHashtag,
  RiPercentLine,
  RiInformationLine,
  RiShieldCheckLine,
  RiFileCopyLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/buyer`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

// ─── Sub-components ───────────────────────────────────────────────────────────

const Section = ({ title, icon: Icon, children, accent = "blue" }) => {
  const colors = {
    blue: "border-blue-200 bg-blue-50/40",
    indigo: "border-indigo-200 bg-indigo-50/40",
    emerald: "border-emerald-200 bg-emerald-50/40",
    amber: "border-amber-200 bg-amber-50/40",
    slate: "border-slate-200 bg-slate-50/40",
  };
  const iconColors = {
    blue: "text-blue-500 bg-blue-100",
    indigo: "text-indigo-500 bg-indigo-100",
    emerald: "text-emerald-500 bg-emerald-100",
    amber: "text-amber-500 bg-amber-100",
    slate: "text-slate-500 bg-slate-100",
  };
  return (
    <div className={`rounded-2xl border ${colors[accent]} overflow-hidden`}>
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-current border-opacity-20">
        <div
          className={`w-7 h-7 rounded-lg flex items-center justify-center ${iconColors[accent]}`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <h3 className="text-sm font-bold text-slate-700 tracking-wide uppercase">
          {title}
        </h3>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
};

const InfoRow = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-xs font-medium text-slate-400 uppercase tracking-wider whitespace-nowrap">
      {label}
    </span>
    <span
      className={`text-sm font-semibold text-slate-700 text-right ${
        mono ? "font-mono" : ""
      }`}
    >
      {value || "—"}
    </span>
  </div>
);

const StatCard = ({ label, value, icon: Icon, color }) => {
  const palette = {
    blue: "bg-blue-600 shadow-blue-200",
    emerald: "bg-emerald-500 shadow-emerald-200",
    indigo: "bg-indigo-500 shadow-indigo-200",
    amber: "bg-amber-500 shadow-amber-200",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-center gap-4">
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-lg ${palette[color]}`}
      >
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-xs text-slate-400 font-medium uppercase tracking-widest">
          {label}
        </p>
        <p className="text-lg font-bold text-slate-800 mt-0.5">{value}</p>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BuyerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/get/${id}`);
        setOrder(res.data?.data);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load order");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const handlePrint = () => window.print();

  // ── Loading ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-blue-500">
          <AiOutlineLoading3Quarters className="w-10 h-10 animate-spin" />
          <p className="text-sm font-medium text-slate-500">
            Loading order details…
          </p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────

  if (error || !order) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-50 border border-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <RiInformationLine className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-slate-700 font-semibold mb-1">
            {error || "Order not found"}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-sm text-blue-600 hover:underline flex items-center gap-1 mx-auto"
          >
            <RiArrowLeftLine className="w-4 h-4" /> Go back
          </button>
        </div>
      </div>
    );
  }

  const bank = order.companyId;
  const company = order.sellerCompanyId;

  return (
    <>
      {/* ── Print Styles ── */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-area { box-shadow: none !important; }
        }
      `}</style>

      <div className="min-h-screen bg-slate-50 print-area">
        {/* ── Top Bar ── */}
        <header className="no-print bg-white border-b border-slate-100 sticky top-0 z-20 shadow-sm">
          <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-blue-600 transition"
            >
              <RiArrowLeftLine className="w-4 h-4" />
              Back to Orders
            </button>

            <div className="flex items-center gap-3">
              {/* Invoice badge */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                <RiFileCopyLine className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs font-mono font-bold text-blue-700">
                  {order.invoiceNumber}
                </span>
              </div>

              {/* <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold transition shadow"
              >
                <RiPrinterLine className="w-4 h-4" />
                Print
              </button> */}
            </div>
          </div>
        </header>

        <div ref={printRef} className="w-full mx-auto px-6 py-8 space-y-6">
          {/* ── Page Title ── */}
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200 flex-shrink-0">
                  <RiShoppingCartLine className="w-5 h-5" />
                </span>
                Order Details
              </h1>
              <p className="text-sm text-slate-400 mt-1 ml-[52px]">
                Created on {fmtDate(order.createdAt)}
              </p>
            </div>

            {/* Status pill */}
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
              <RiCheckboxCircleLine className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-semibold text-emerald-700">
                Order Confirmed
              </span>
            </div>
          </div>

          {/* ── Stat Cards ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Grand Total"
              value={`₹${fmt(order.grandTotal)}`}
              icon={RiMoneyDollarCircleLine}
              color="blue"
            />
            <StatCard
              label="Total Items"
              value={`${order.products?.length || 0} product${order.products?.length !== 1 ? "s" : ""}`}
              icon={RiShoppingCartLine}
              color="indigo"
            />
            <StatCard
              label="Total Tax"
              value={`₹${fmt((order.totalCgst || 0) + (order.totalSgst || 0))}`}
              icon={RiPercentLine}
              color="amber"
            />
            <StatCard
              label="Invoice Date"
              value={new Date(order.createdAt).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
              icon={RiCalendarLine}
              color="emerald"
            />
          </div>

          {/* ── Two-column layout ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ── Main Bank (Buyer) ── */}
            <Section title="Main Bank (Buyer)" icon={RiBankLine} accent="blue">
              <InfoRow label="Bank Name" value={bank?.bankName} />
              <InfoRow label="Head Office" value={bank?.headOffice} />
              <InfoRow label="Contact Person" value={bank?.contactPerson} />
              {/* <InfoRow label="Designation" value={bank?.designation} /> */}
              {/* <InfoRow label="Department" value={bank?.department} /> */}
              <InfoRow label="Phone" value={bank?.phone} />
              <InfoRow label="Email" value={bank?.email} />
              <InfoRow label="Address" value={bank?.address} />
              {/* <InfoRow label="PIN" value={bank?.pin} mono /> */}
              <InfoRow label="GST No." value={bank?.gstno} mono />
              <InfoRow label="Customer ID" value={bank?.customer_id} mono />
            </Section>

            {/* ── Customer Info ── */}
            <Section
              title="Customer Information"
              icon={RiUserLine}
              accent="indigo"
            >
              <InfoRow label="Name" value={order.userName} />
              <InfoRow label="Phone" value={order.phone} />
              <InfoRow label="Address" value={order.address} />
            </Section>

            {/* ── Seller Company ── */}
            <Section
              title="Seller Company"
              icon={RiBuildingLine}
              accent="emerald"
            >
              <InfoRow label="Company Name" value={company?.companyName} />
              <InfoRow label="Owner Name" value={company?.ownerName} />
              <InfoRow label="PAN" value={company?.pan} mono />
              <InfoRow label="GST" value={company?.gst} mono />
            </Section>

            {/* ── Bank Account Used ── */}
            <Section
              title="Bank Account Used"
              icon={RiShieldCheckLine}
              accent="amber"
            >
              <InfoRow
                label="Account Number"
                value={order.selectedBankAccount?.accountNumber}
                mono
              />
              <InfoRow
                label="IFSC Code"
                value={order.selectedBankAccount?.ifscCode}
                mono
              />
              <InfoRow
                label="Branch Name"
                value={order.selectedBankAccount?.branchName}
              />
              <InfoRow
                label="Branch Address"
                value={order.selectedBankAccount?.branchAddress}
              />
            </Section>
          </div>

          {/* ── Query Contact (if present) ── */}
          {order.selectedQueryContact?.contactPersonName && (
            <Section title="Query Contact" icon={RiUserStarLine} accent="slate">
              <div className="grid grid-cols-2 gap-4">
                <InfoRow
                  label="Contact Person"
                  value={order.selectedQueryContact.contactPersonName}
                />
                <InfoRow
                  label="Phone"
                  value={order.selectedQueryContact.phone}
                />
              </div>
            </Section>
          )}

          {/* ── Products Table ── */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 bg-slate-50">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-blue-100 text-blue-500">
                <RiShoppingCartLine className="w-4 h-4" />
              </div>
              <h3 className="text-sm font-bold text-slate-700 tracking-wide uppercase">
                Ordered Products
              </h3>
              <span className="ml-auto text-xs text-slate-400 font-medium">
                {order.products?.length} item
                {order.products?.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {[
                      ["#", "w-10 text-center"],
                      ["Product", ""],
                      ["Size", ""],
                      ["Price/Pc", "text-right"],
                      ["Qty", "text-right"],
                      ["Taxable", "text-right"],
                      ["CGST", "text-right"],
                      ["SGST", "text-right"],
                      ["Total", "text-right"],
                    ].map(([col, cls]) => (
                      <th
                        key={col}
                        className={`px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-widest ${cls}`}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {order.products?.map((item, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-slate-50 hover:bg-blue-50/30 transition"
                    >
                      <td className="px-4 py-3.5 text-center">
                        <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold flex items-center justify-center mx-auto">
                          {idx + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 font-semibold text-slate-800">
                        {item.productId?.name || item.productId || "—"}
                        {item.productId?.hsnCode && (
                          <p className="text-[10px] font-normal text-slate-400 mt-0.5">
                            HSN: {item.productId.hsnCode}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-semibold">
                          {item.size}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right font-mono text-slate-700">
                        ₹{fmt(item.pricePerUnit)}
                      </td>
                      <td className="px-4 py-3.5 text-right font-semibold text-slate-800">
                        {item.quantity}
                      </td>
                      <td className="px-4 py-3.5 text-right font-mono text-slate-700">
                        ₹{fmt(item.taxableAmount)}
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="text-emerald-600 font-mono text-xs">
                          ₹{fmt(item.cgstAmount)}
                        </span>
                        <p className="text-[10px] text-slate-400">
                          {item.cgstPercentage}%
                        </p>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="text-emerald-600 font-mono text-xs">
                          ₹{fmt(item.sgstAmount)}
                        </span>
                        <p className="text-[10px] text-slate-400">
                          {item.sgstPercentage}%
                        </p>
                      </td>
                      <td className="px-4 py-3.5 text-right font-bold text-blue-700 font-mono">
                        ₹{fmt(item.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Order Totals ── */}
            <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/60">
              <div className="max-w-xs ml-auto space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotal</span>
                  <span className="font-semibold font-mono text-slate-700">
                    ₹{fmt(order.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total CGST</span>
                  <span className="font-semibold font-mono text-emerald-600">
                    ₹{fmt(order.totalCgst)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Total SGST</span>
                  <span className="font-semibold font-mono text-emerald-600">
                    ₹{fmt(order.totalSgst)}
                  </span>
                </div>
                <div className="border-t border-slate-200 pt-2 flex justify-between">
                  <span className="font-bold text-slate-800">Grand Total</span>
                  <span className="font-bold text-xl text-blue-700 font-mono">
                    ₹{fmt(order.grandTotal)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Note ── */}
          {order.note && (
            <Section title="Note" icon={RiStickyNoteLine} accent="slate">
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">
                {order.note}
              </p>
            </Section>
          )}

          {/* ── Footer meta ── */}
          <div className="flex items-center justify-between text-xs text-slate-400 py-2 border-t border-slate-100">
            <span className="flex items-center gap-1.5">
              <RiHashtag className="w-3.5 h-3.5" />
              Order ID: {order._id}
            </span>
            <span className="flex items-center gap-1.5">
              <RiCalendarLine className="w-3.5 h-3.5" />
              Last updated: {fmtDate(order.updatedAt)}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
